import React from 'react';
import _ from 'lodash';
import {
  Input,
  Button,
  InputGroup,
  UncontrolledTooltip,
  Alert,
} from 'reactstrap';
import PropTypes from 'prop-types';
import { propTypes } from 'formsy-react';
import {
  faMagnifyingGlass,
  faCircleXmark,
  faPen,
  faSpinner,
  faCopy,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import { resolveGtin as resolveGtinValidoo } from 'app/shared/actions/generated/validoo-article.actions';
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import { resolveGtin as resolveGtinDabas } from 'app/shared/actions/dabas.actions';

import domainModel from 'domain-model';

import withFormsyValidations from '../withFormsyValidations';

const states = {
  empty: {
    icon: faPen,
    color: 'grey',
    tooltip: 'Skriv in GTIN för att söka efter artikel',
    onClick: () => {},
  },
  search: {
    color: 'primary',
    icon: faMagnifyingGlass,
    tooltip: 'Sök efter artikel',
    onClick: () => {},
  },
  loading: {
    color: 'info',
    icon: faSpinner,
    tooltip: 'Söker...',
  },
  loaded: {
    color: 'primary',
    icon: faCopy,
    tooltip: 'Information hittad, klicka för att fylla i formuläret',
  },
  error: {
    color: 'danger',
    icon: faCircleXmark,
    tooltip: 'Fel',
  },
  copied: {
    color: 'success',
    icon: faCheck,
  },
  discontinued: {
    tooltip: 'Varan har utgått. Klicka för att fylla i formuläret',
    color: 'warning',
    icon: faCircleXmark,
    onClick: () => {},
  },
  notFound: {
    tooltip: 'Kunde inte hitta varan',
    color: 'warning',
    icon: faCircleXmark,
  },
};

class CheckInValidoo extends React.Component {
  static get propTypes() {
    return {
      GTIN: PropTypes.string.isRequired,
      baseGTIN: PropTypes.string.isRequired,
      caseGTIN: PropTypes.string,
      palletGTIN: PropTypes.string,
      owningSupplierOrganizationId: PropTypes.string.isRequired,
      isValid: PropTypes.bool.isRequired,
      onUseExternalData: PropTypes.func.isRequired,
      onLoaded: PropTypes.func.isRequired,
      id: PropTypes.string.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.loadData = this.loadData.bind(this);
    this.verifyState = this.verifyState.bind(this);
    this.state = {
      state: states.empty,
    };
  }

  componentDidUpdate(prevProps) {
    this.verifyState(prevProps);
  }

  verifyState(prevProps) {
    if (
      prevProps.GTIN === this.props.GTIN &&
      prevProps.isValid === this.props.isValid
    ) {
      return;
    }
    const { GTIN, isValid } = this.props;
    const { state } = this.state;
    if ((!GTIN || !isValid) && state !== states.empty) {
      this.setState({ state: states.empty });
      return;
    }
    if (GTIN && isValid && state !== states.search) {
      this.setState({ state: states.search });
      return;
    }

    if (GTIN && GTIN.length === 13 && !isValid && state !== states.search) {
      this.setState({ state: states.search });
    }
  }

  async loadData() {
    const {
      baseGTIN,
      caseGTIN,
      palletGTIN,
      owningSupplierOrganizationId,
      onLoaded,
    } = this.props;
    this.setState({ state: states.loading });
    let validooResponse;
    let validooErrorStatus = null;
    let dabasErrorStatus = null;
    try {
      validooResponse = await resolveGtinValidoo({
        baseGTIN,
        caseGTIN,
        palletGTIN,
        owningSupplierOrganizationId,
      });

      if (_.get(validooResponse, 'data.otherAttributes.isDiscontinued')) {
        this.setState({ state: states.discontinued });
        return;
      }
      if (validooResponse.errorMessage) {
        this.setState({ state: states.error });
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      validooErrorStatus = err.response.status;
    }

    let dabasResponse;
    try {
      dabasResponse = await resolveGtinDabas({
        GTIN: baseGTIN,
      });
    } catch (err) {
      dabasErrorStatus = err.response.status;
    }

    if (dabasErrorStatus === 404 && validooErrorStatus === 404) {
      this.setState({ state: states.notFound });
      return;
    }

    if (dabasErrorStatus && validooErrorStatus) {
      this.setState({ state: states.error });
      return;
    }

    const data = { ...dabasResponse?.data, ...validooResponse?.data };
    // ugly fix so we don't show the loaded tooltip when we have no data
    if (_.isEmpty(data) || Object.keys(data).length < 3) {
      this.setState({ state: states.notFound });
      onLoaded(data);
      return;
    }
    this.setState({ articleData: data, state: states.loaded });
    onLoaded(data);
  }

  render() {
    const { onUseExternalData, id } = this.props;
    const { articleData, state } = this.state || {};

    return (
      <>
        <Button
          color={state.color}
          id={`Tooltip-${id}`}
          onClick={() => {
            if (state === states.loaded) {
              onUseExternalData(articleData);
              this.setState({ state: states.copied });
              return;
            }
            if (state === states.search) {
              this.loadData();
            }
          }}
        >
          <FontAwesomeIcon icon={state.icon} spin={state === states.loading} />
        </Button>
        {state.tooltip && (
          <UncontrolledTooltip target={`Tooltip-${id}`}>
            {state.tooltip}
          </UncontrolledTooltip>
        )}
      </>
    );
  }
}

class GTINInput extends React.Component {
  static get propTypes() {
    return {
      name: PropTypes.string.isRequired,
      owningSupplierOrganizationId: PropTypes.string.isRequired,
      className: PropTypes.string,
      setValue: PropTypes.func.isRequired,
      required: PropTypes.bool,
      disabled: PropTypes.bool,
      showImportButton: PropTypes.bool,
      onChange: PropTypes.func.isRequired,
      ...propTypes,
    };
  }

  static get defaultProps() {
    return {
      required: false,
      disabled: false,
      showImportButton: false,
      className: '',
    };
  }

  constructor(props) {
    super(props);
    this.changeValue = this.changeValue.bind(this);
    this.checkAndFixLength = this.checkAndFixLength.bind(this);
    this.useExternalData = this.useExternalData.bind(this);
    this.onLoaded = this.onLoaded.bind(this);
    this.state = { infoMessage: '', errorMessage: '' };
  }

  changeValue(event) {
    const { name, setValue, onChange } = this.props;
    const { value } = event.currentTarget;
    setValue(value, false); // this is the performance thief 5053990107384
    onChange(name, value);
  }

  async checkAndFixLength() {
    const { name, value, onChange } = this.props;
    if (value && value.length === 13) {
      await onChange(name, _.padStart(value, 14, '0'));
    }
  }

  onLoaded(articleData) {
    this.setState({
      infoMessage: articleData.infoMessage,
      errorMessage: articleData.errorMessage,
    });
  }

  async useExternalData(articleData) {
    const { onChange, article } = this.props;
    if (articleData.alcoholPercentage) {
      await onChange('alcoholPercentage', articleData.alcoholPercentage);
    }
    if (articleData.shelfLife) {
      await onChange('shelfLife', articleData.shelfLife);
    }
    if (articleData.productCategory) {
      await onChange('productCategory', articleData.productCategory);
    }
    if (articleData.shortName) {
      await onChange('shortName', articleData.shortName);
    }

    if (articleData.name) {
      await onChange('name', articleData.name);
    }
    if (articleData.description) {
      await onChange('description', articleData.description);
    }
    if (articleData.ingredientsDeclaration) {
      await onChange(
        'ingredientsDeclaration',
        articleData.ingredientsDeclaration,
      );
    }
    if (articleData.volume) {
      await onChange('volume', articleData.volume);
    }
    if (articleData.weight) {
      await onChange('weight', articleData.weight);
    }
    if (articleData.netWeight) {
      await onChange('netWeight', articleData.netWeight);
    }
    if (articleData.caseNetWeight) {
      await onChange('caseNetWeight', articleData.caseNetWeight);
    }
    if (articleData.caseWeight) {
      await onChange('caseWeight', articleData.caseWeight);
    }
    if (articleData.container) {
      await onChange('container', articleData.container);
    }

    if (articleData.images) {
      if (!article.image) {
        await onChange('image', articleData.images[0]);
        await onChange('images', articleData.images);
      } else {
        // we need to work on our image handling, this is hacky
        (article.images || []).unshift(article.image);
        await onChange('images', _.union(article.images, articleData.images));
      }
    }

    if (articleData.origin) {
      await onChange('origin', articleData.origin);
    }
    if (articleData.GTIN) {
      await onChange('GTIN', articleData.GTIN);
    }
    if (articleData.boxGTIN) {
      await onChange('boxGTIN', articleData.boxGTIN);
    }
    if (articleData.palletGTIN) {
      await onChange('palletGTIN', articleData.palletGTIN);
    }
    if (articleData.nutritionDeclaration) {
      await onChange('nutritionDeclaration', articleData.nutritionDeclaration);
    }
    if (articleData.VAT) {
      await onChange('VAT', articleData.VAT);
    }
    if (articleData.recycleGTIN) {
      await onChange('recycleGTIN', articleData.recycleGTIN);
    }
    if (articleData.HSCode) {
      await onChange('HSCode', articleData.HSCode);
    }
    if (articleData.colliSize) {
      await onChange('colliSize', articleData.colliSize);
    }
    if (articleData.caseUnitSize) {
      await onChange('caseUnitSize', articleData.caseUnitSize);
    }
    if (articleData.caseUnitsPerPallet) {
      await onChange('caseUnitsPerPallet', articleData.caseUnitsPerPallet);
    }
    if (articleData.caseUnitsPerPalletLayer) {
      await onChange(
        'caseUnitsPerPalletLayer',
        articleData.caseUnitsPerPalletLayer,
      );
    }
    if (articleData.HSCode) {
      await onChange('HSCode', articleData.HSCode);
    }
  }

  render() {
    // Dont remove onChange from below.
    const {
      value,
      name,
      id,
      className,
      article,
      isValid,
      required,
      disabled,
      owningSupplierOrganizationId,
    } = this.props;

    const { infoMessage, errorMessage } = this.state;
    return (
      <div>
        <InputGroup>
          <Input
            invalid={!isValid}
            type="string"
            id={id}
            className={className}
            required={required}
            disabled={disabled}
            value={value}
            name={name}
            onChange={this.changeValue}
            onBlur={this.checkAndFixLength}
          />
          <CheckInValidoo
            id={`${id}-check`}
            isValid={isValid}
            GTIN={value}
            baseGTIN={article.GTIN}
            caseGTIN={article.boxGTIN}
            palletGTIN={article.palletGTIN}
            onLoaded={this.onLoaded}
            onUseExternalData={this.useExternalData}
            owningSupplierOrganizationId={owningSupplierOrganizationId}
          />
        </InputGroup>
        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
        {infoMessage && <Alert color="warning">{infoMessage}</Alert>}
      </div>
    );
  }
}

export default withFormsyValidations(GTINInput, () => ({
  GTIN(values, value) {
    if (!value) return true;
    return value.length === 14 && domainModel.article.isValidGTIN(value);
  },
}));
